// Champion ("legend" in the sim) — pure client-side projections of the
// snapshot. Framework-free so every rule is unit-testable.
//
// The module is gated by the snapshot itself: `state.globals.players[i].legend`
// exists only while `legends.enabled` is on, so `localLegend() === null`
// means "render nothing". Wire types: packages/protocol/src/legend.ts.
//
// Every legend balance number comes from the snapshot's `globals.legendRows`
// (single source of truth: packages/sim/src/content/legend_rows.rs). It is
// present only while `legends.enabled` is on; absent = render nothing.

import type {
  GameSnapshot,
  LegendKindRow,
  LegendPlayerState,
  LegendRows,
  LegendUnitProp,
  SceneNode,
} from '@aod/protocol';

/** Sim basis-point denominator (legend_rows::BP). */
const BP = 10_000;

/** The snapshot's balance rows, or null while the module is off. */
export function legendRows(snapshot: GameSnapshot | null): LegendRows | null {
  const g = snapshot?.state?.globals as { legendRows?: LegendRows } | undefined;
  return g?.legendRows ?? null;
}

/** Kills needed to reach `level` (index = level - 1); null at/above the cap. */
export function killsForLevel(rows: LegendRows, level: number): number | null {
  if (level > rows.maxLevel) return null;
  return rows.killsForLevel[Math.max(0, level - 1)] ?? null;
}

/** Re-summon cost multiplier in percent after `deaths` deaths (index clamped like the sim's `_` arm). */
export function resummonCostPct(rows: LegendRows, deaths: number): number {
  const bp = rows.resummonCostBp;
  return (bp[Math.min(Math.max(0, deaths), bp.length - 1)] ?? BP) / (BP / 100);
}

export interface SummonCost {
  readonly gold: number;
  readonly rare: number;
  readonly pop: number;
  readonly ticks: number;
}

/** legend_rows::scale_bp rounding (half up). */
function scaleBp(amount: number, bp: number): number {
  return Math.floor((amount * bp + BP / 2) / BP);
}

export function summonCost(rows: LegendRows, row: LegendKindRow, deaths: number): SummonCost {
  const bp = rows.resummonCostBp;
  const cost = bp[Math.min(Math.max(0, deaths), bp.length - 1)] ?? BP;
  return {
    gold: scaleBp(row.gold, cost),
    rare: scaleBp(row.rare, cost),
    pop: row.pop,
    ticks: row.summonTicks,
  };
}

/** Doctrine indices in `globals.players[i].doctrine.spent` (sim `Archetype` order; UI-only mapping of the rows' doctrine names). */
const DOCTRINE_INDEX: Readonly<Record<string, number>> = {
  industry: 0,
  science: 1,
  faith: 2,
  arcane: 3,
  nature: 4,
  chaos: 5,
};

/** Highest doctrine tier (points spent) among the doctrines that offer `row`. */
export function doctrineTier(
  snapshot: GameSnapshot | null,
  playerIndex: number,
  row: LegendKindRow,
): number {
  const spent = players(snapshot).find((p) => p.playerIndex === playerIndex)?.doctrine?.spent;
  if (!spent) return 0;
  return Math.max(0, ...row.doctrines.map((d) => spent[DOCTRINE_INDEX[d] ?? 0] ?? 0));
}

/** Doctrine names in `DOCTRINE_INDEX` order (index = sim `Archetype`). */
const DOCTRINE_NAMES = Object.keys(DOCTRINE_INDEX);

/** The doctrine with the most points spent (ties: lowest index, like the sim); null with none spent. */
function dominantDoctrine(snapshot: GameSnapshot | null, playerIndex: number): string | null {
  const spent = players(snapshot).find((p) => p.playerIndex === playerIndex)?.doctrine?.spent;
  if (!spent) return null;
  let best: string | null = null;
  let bestPts = 0;
  for (const name of DOCTRINE_NAMES) {
    const pts = spent[DOCTRINE_INDEX[name] ?? 0] ?? 0;
    if (pts > bestPts) {
      best = name;
      bestPts = pts;
    }
  }
  return best;
}

/**
 * Which kind the player is offered (mirrors summon.rs `offered_row`, rule
 * `dominant_doctrine`): the kind fixed by an earlier summon, else the snapshot's
 * `legend.offered`, else (doctrine tier not reached yet, so the checklist can
 * show the gap) the dominant doctrine's fixed default from `defaultFor`, else
 * the first row. Older snapshots without `offered` use the first row whose
 * doctrine tier is met.
 */
export function offeredKind(
  snapshot: GameSnapshot | null,
  rows: LegendRows,
  legend: LegendPlayerState,
  playerIndex: number,
): { readonly kind: string; readonly row: LegendKindRow } | null {
  const named = (kind: string | null | undefined) => {
    const row = kind ? rows.kinds[kind] : undefined;
    return kind && row ? { kind, row } : null;
  };
  const fixed = named(legend.kind) ?? named(legend.offered);
  if (fixed) return fixed;
  const entries = Object.entries(rows.kinds);
  const dominant = dominantDoctrine(snapshot, playerIndex);
  const byDefault = dominant
    ? entries.find(([, r]) => r.defaultFor?.includes(dominant))
    : undefined;
  if (byDefault) return { kind: byDefault[0], row: byDefault[1] };
  const met = entries.find(([, r]) => doctrineTier(snapshot, playerIndex, r) >= r.minDoctrineTier);
  const pick = met ?? entries[0];
  return pick ? { kind: pick[0], row: pick[1] } : null;
}

interface RawPlayer {
  readonly playerIndex: number;
  readonly legend?: LegendPlayerState;
  readonly doctrine?: { readonly spent: ReadonlyArray<number> };
  readonly treasury?: { readonly gold: number; readonly faith?: number };
}

function players(snapshot: GameSnapshot | null): ReadonlyArray<RawPlayer> {
  const g = snapshot?.state.globals as { players?: ReadonlyArray<RawPlayer> } | undefined;
  return g?.players ?? [];
}

/** The local player's champion state, or null when the module is off. */
export function localLegend(
  snapshot: GameSnapshot | null,
  playerIndex: number,
): LegendPlayerState | null {
  return players(snapshot).find((p) => p.playerIndex === playerIndex)?.legend ?? null;
}

/**
 * The Lich's summon (phylactery) city, `city_<n>`, or null. The protocol type
 * now spells it `phylacteryCityId` (it used to declare the typo
 * `phylacheryCityId`), so snapshots captured before the fix still carry the
 * typo'd key — keep reading it as a fallback.
 */
export function phylacteryCityOf(legend: LegendPlayerState): string | null {
  const raw = legend as unknown as Record<string, string | null | undefined>;
  return raw.phylacteryCityId ?? raw.phylacheryCityId ?? null;
}

/** The unit's champion prop (absent = not a champion). */
export function unitLegend(unit: SceneNode): LegendUnitProp | null {
  return (unit.props?.legend as LegendUnitProp | undefined) ?? null;
}

export function playerGold(snapshot: GameSnapshot | null, playerIndex: number): number {
  return players(snapshot).find((p) => p.playerIndex === playerIndex)?.treasury?.gold ?? 0;
}

export function playerFaith(snapshot: GameSnapshot | null, playerIndex: number): number {
  return players(snapshot).find((p) => p.playerIndex === playerIndex)?.treasury?.faith ?? 0;
}

export type GateKey = 'available' | 'doctrine' | 'cityTier' | 'site' | 'pop' | 'gold' | 'rare';

export interface GateItem {
  readonly key: GateKey;
  readonly met: boolean;
  /** Numbers for the label ("have / need"), when meaningful. */
  readonly have?: number;
  readonly need?: number;
  /** `rare` only: the pool the sim draws from (`rows.magicEnabled`: mana, else faith). */
  readonly currency?: 'mana' | 'faith';
}

export interface SummonGate {
  readonly items: ReadonlyArray<GateItem>;
  /** The first unmet gate, or null when the summon button may be enabled. */
  readonly blocker: GateKey | null;
  readonly cost: SummonCost;
}

export interface SummonGateInput {
  readonly rows: LegendRows;
  readonly row: LegendKindRow;
  readonly legend: LegendPlayerState;
  readonly doctrineTier: number;
  readonly gold: number;
  readonly faith: number;
  readonly cityTier: number;
  /** The city's current population (the summon spends `row.pop` of it). */
  readonly cityPop: number;
  readonly completedBuildings: ReadonlyArray<string>;
}

/** Whether the city-tier minimum gates a summon after `deaths` deaths (first summon only). */
export function cityTierApplies(rows: LegendRows, deaths: number): boolean {
  return deaths <= rows.cityTierGateMaxDeaths;
}

/** radiant + nether, the pool a summon draws the rare input from when magic is on. */
export function manaTotal(legend: LegendPlayerState): number {
  return legend.mana ? legend.mana.radiant + legend.mana.nether : 0;
}

/**
 * The summon requirements checklist for one city (D4), mirroring
 * summon.rs `try_start_summon`: gold, the rare input and city population are
 * all checked there (-> `cannot_afford`). The rare input is mana
 * (radiant + nether) when `rows.magicEnabled` and faith otherwise, so the
 * gate is exact. The city-tier minimum applies to the first summon only
 * (`rows.cityTierGateMaxDeaths`, mirroring `legend_rows::city_tier_applies`).
 */
export function summonGate(input: SummonGateInput): SummonGate {
  const { row, legend } = input;
  const cost = summonCost(input.rows, row, legend.deaths);
  const currency = input.rows.magicEnabled ? 'mana' : 'faith';
  const haveRare = currency === 'mana' ? manaTotal(legend) : input.faith;
  const items: GateItem[] = [
    { key: 'available', met: legend.state === 'dormant' },
    {
      key: 'doctrine',
      met: input.doctrineTier >= row.minDoctrineTier,
      have: input.doctrineTier,
      need: row.minDoctrineTier,
    },
    {
      key: 'cityTier',
      met: !cityTierApplies(input.rows, legend.deaths) || input.cityTier >= row.minCityTier,
      have: input.cityTier,
      need: row.minCityTier,
    },
    { key: 'site', met: input.completedBuildings.includes(row.siteBuilding) },
    { key: 'pop', met: input.cityPop >= cost.pop, have: input.cityPop, need: cost.pop },
    { key: 'gold', met: input.gold >= cost.gold, have: input.gold, need: cost.gold },
    { key: 'rare', met: haveRare >= cost.rare, have: haveRare, need: cost.rare, currency },
  ];
  return { items, blocker: items.find((i) => !i.met)?.key ?? null, cost };
}

/** Summon project progress 0..100 from the sim tick (clamped). */
export function summonProgressPct(
  doneTick: number | null,
  tick: number,
  totalTicks: number,
): number {
  if (doneTick === null || totalTicks <= 0) return 0;
  const total = totalTicks;
  const pct = ((total - (doneTick - tick)) / total) * 100;
  return Math.max(0, Math.min(100, Math.round(pct)));
}

/** Ticks left until `target` (never negative; 0 when null). */
export function ticksLeft(target: number | null, tick: number): number {
  return target === null ? 0 : Math.max(0, target - tick);
}

/** Sim ticks → whole seconds at the base 10 ticks/s. */
export function ticksToSeconds(ticks: number): number {
  return Math.ceil(ticks / 10);
}

/** "1:05" style clock for a tick span. */
export function formatTicksClock(ticks: number): string {
  const s = ticksToSeconds(ticks);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** Hex distance on axial coordinates. */
export function hexDistance(aq: number, ar: number, bq: number, br: number): number {
  const dq = aq - bq;
  const dr = ar - br;
  return Math.max(Math.abs(dq), Math.abs(dr), Math.abs(dq + dr));
}

/** Nodes a minimap marker is drawn for: every visible champion + the summoning city. */
export interface ChampionMarker {
  readonly q: number;
  readonly r: number;
  readonly ownerId: number;
  readonly summoning: boolean;
  /** dragon / golem / lich: picks the glyph (a rival's unit shows its own kind). */
  readonly kind: string;
}

export function championMarkers(
  snapshot: GameSnapshot | null,
  localPlayerIndex: number,
): ReadonlyArray<ChampionMarker> {
  if (!snapshot || !localLegend(snapshot, localPlayerIndex)) return [];
  const out: ChampionMarker[] = [];
  const nodes = snapshot.scene.children ?? [];
  for (const n of nodes) {
    const q = n.props?.q as number | undefined;
    const r = n.props?.r as number | undefined;
    if (q === undefined || r === undefined) continue;
    if (n.type === 'unit' && unitLegend(n)) {
      const kind = unitLegend(n)?.kind ?? 'dragon';
      out.push({
        q,
        r,
        ownerId: (n.props?.ownerId as number | undefined) ?? 0,
        summoning: false,
        kind,
      });
    }
  }
  const mine = localLegend(snapshot, localPlayerIndex);
  if (mine?.state === 'summoning' && mine.cityId) {
    const city = nodes.find((n) => n.id === mine.cityId);
    const q = city?.props?.q as number | undefined;
    const r = city?.props?.r as number | undefined;
    if (q !== undefined && r !== undefined) {
      out.push({
        q,
        r,
        ownerId: localPlayerIndex,
        summoning: true,
        kind: mine.kind ?? mine.offered ?? 'dragon',
      });
    }
  }
  return out;
}
